import { useShowPolicyBanner, wishlistsApi } from "@capone/common";
import { STAYS_WISHLIST } from "@capone/experiments";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  genericPriceFormatter,
  getPricesWithComma,
  getTotalPriceText,
  HotelAvailabilityMapTooltip,
  Icon,
  IconName,
  isSignificantSavings,
} from "halifax";
import React, { useContext } from "react";
import { useSelector } from "react-redux";
import {
  CorpLodging,
  HOTEL_LIST_MAP_CAROUSEL,
  HotelStarRatingEnum,
  Lodging,
  LodgingCollectionEnum,
  LodgingPromotionType,
  MapFunnelEnum,
  RewardsAccount,
  ViewedCorpRateDescriptorEntryPoints,
} from "redmond";
import { StayType } from "redmond/hotels-module/interfaces";
import { config } from "../../../../../api/config";
import { trackEvent } from "../../../../../api/v0/analytics/trackEvent";
import { ClientContext } from "../../../../../App";
import {
  AVAILABLE,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  LC_GATING_EXPERIMENT,
  LODGING_PROMOTIONS,
  LODGING_PROMOTIONS_AVAILABLE,
  LODGING_PROMOTIONS_VARIANTS,
  LUXURY_PAY_AT_HOTEL_BADGE,
  ALLOWED_PAY_LATER_LODGINGS,
  AllowedPayLaterLodgingsConfig,
  getExperimentVariantJSON,
  PREMIER_COLLECTION_EXPERIMENT,
  useExperiments,
} from "../../../../../context/experiments";
import { isAllowedPayLaterLodging } from "../../../../../utils/payLater";
import { onOpenCompareBarTooltip } from "../../../../../utils/events";
import { WishlistToggleHeart } from "../../../../wishlist/components/WishlistToggleHeart/component";
import { getMarkerPrice } from "../utils";

interface IListingMarker {
  lat: number;
  lng: number;
  zIndex: number; // **Important** This prop is used by google-maps-react-markers during the rendering of the markers
  lodging: Lodging;
  roomsCount: number;
  isVentureX: boolean;
  searchedNightCount: number;
  largestValueAccount: RewardsAccount;
  rewardsKey: string | undefined;
  isSelected: boolean;
  forceHoverState?: boolean;
  disableMapCard?: boolean; // Hide the marker card when clicked
  onMarkerHover?: (markerId: string) => void;
  onMarkerClick?: (markerId: string, lat: number, lng: number) => void;
  onMarkerClose: (e: React.MouseEvent<Element, MouseEvent>) => void;
  onMarkerDetailsClick: () => void;
}

export const ListingMarker = ({
  lat,
  lng,
  lodging,
  roomsCount,
  isVentureX,
  searchedNightCount,
  rewardsKey,
  largestValueAccount,
  isSelected,
  forceHoverState,
  disableMapCard,
  onMarkerHover,
  onMarkerClick,
  onMarkerClose,
  onMarkerDetailsClick,
}: IListingMarker) => {
  const { price } = lodging;
  const { policies } = useContext(ClientContext);

  const expState = useExperiments();

  // Experiments
  const isPremierCollectionEnabled =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT
    ) === AVAILABLE;

  const isLifestyleCollectionEnabled =
    getExperimentVariant(expState.experiments, LC_GATING_EXPERIMENT) ===
    AVAILABLE;

  const lodgingPromotions = getExperimentVariantCustomVariants(
    expState.experiments,
    LODGING_PROMOTIONS,
    LODGING_PROMOTIONS_VARIANTS
  );

  const isLodgingPromotionsExperiment =
    lodgingPromotions === LODGING_PROMOTIONS_AVAILABLE;

  const isPayAtHotelBadgeExperimentEnabled =
    getExperimentVariant(expState.experiments, LUXURY_PAY_AT_HOTEL_BADGE) ===
    AVAILABLE;

  const allowedPayLaterLodgingIds = React.useMemo(() => {
    const config = getExperimentVariantJSON(
      expState.experiments,
      ALLOWED_PAY_LATER_LODGINGS
    ) as AllowedPayLaterLodgingsConfig | null;
    return config?.data?.lodgingIds;
  }, [expState.experiments]);

  const isPayAtHotelBadgeEnabled =
    isPayAtHotelBadgeExperimentEnabled &&
    isAllowedPayLaterLodging(allowedPayLaterLodgingIds, lodging.lodging.id);

  const showPCStyling =
    isPremierCollectionEnabled &&
    lodging.lodgingCollection === LodgingCollectionEnum.Premier;

  const showLCStyling =
    (isPremierCollectionEnabled || isLifestyleCollectionEnabled) &&
    lodging.lodgingCollection === LodgingCollectionEnum.Lifestyle;

  const isHome = Boolean(lodging?.homesListingDetails);

  const showOnSaleIcon =
    isLodgingPromotionsExperiment &&
    lodging.bestPromotionThisLodging?.promotionType ===
      LodgingPromotionType.DealOfTheDay &&
    lodging.lodging.tripAdvisorReviews &&
    lodging.lodging.tripAdvisorReviews.overallScore * 5 >= 3.5 &&
    lodging.price?.discountHunting &&
    lodging.price?.discountHunting?.savingsAmount.fiat.value >= 5 &&
    (isVentureX
      ? lodging.lodging.starRating === HotelStarRatingEnum.Four ||
        lodging.lodging.starRating === HotelStarRatingEnum.Five
      : true);

  const showPolicyBanner = useShowPolicyBanner(policies);

  const isCorporate = "corporateTravel" in lodging.lodging;
  const isInPolicy =
    isCorporate &&
    (lodging as CorpLodging).lodging.corporateTravel.policyCompliance
      .isInPolicy;

  const hasCorporate = isCorporate && lodging.hasCorporateRates;

  const isWishlistExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST) === AVAILABLE;

  const isWishlisted = useSelector(
    wishlistsApi.endpoints.getAllWishlistItemsByUser.select()
  )?.data?.has(lodging?.lodging?.id);

  // Marker logic
  const markerPrice = getMarkerPrice(roomsCount, price);

  if (!markerPrice) {
    return null;
  }

  const handleMarkerClick = () => {
    if (onMarkerClick) {
      onMarkerClick(lodging?.lodging?.id, lat, lng);
    }
  };

  const handleMarkerHover = () => {
    if (onMarkerHover) {
      onMarkerHover(lodging?.lodging?.id);
    }
  };

  const renderWishlistIcon = () => {
    return (
      <Icon
        width="15px"
        height="15px"
        name={IconName.WishlistHeart}
        className="icon-fill-red"
      />
    );
  }

  const renderPCLCIcon = () => {
    return isWishlistExperiment && isWishlisted 
      ? renderWishlistIcon() 
      : null;
  }

  const renderIcon = () => {
    if (isWishlistExperiment && isWishlisted) {
      return renderWishlistIcon();
    } else if (isHome) {
      return (
        <Icon className="icon-fill icon-hover-fill" name={IconName.HomeIcon} />
      );
    } else if (showOnSaleIcon) {
      return (
        <Icon
          className="icon-fill icon-hover-fill"
          name={IconName.PreferredHotelIcon}
        />
      );
    } else if (showPolicyBanner && isInPolicy) {
      return (
        <Icon
          width="20px"
          height="20px"
          className="icon-hover-stroke"
          name={IconName.CheckMarkGreen}
        />
      );
    } else if (hasCorporate) {
      return (
        <Icon width="15px" height="15px" name={IconName.StarCircledIcon} />
      );
    } else {
      return null;
    }
  };

  const renderPrice = () => {
    return (
      <Typography
        color="inherit"
        style={{
          fontWeight: "600",
        }}
      >
        {getPricesWithComma(
          getTotalPriceText({
            price: markerPrice,
            priceFormatter: genericPriceFormatter,
          })
        )}
      </Typography>
    );
  };

  return (
    <Box
      className="map-card-overrides"
      display="flex"
      flexDirection="column"
      alignItems="center"
      position={"absolute"}
      style={{
        transform: "translate(-50%, -100%)",
      }}
      onMouseEnter={handleMarkerHover}
    >
      {isSelected && !disableMapCard && (
        // TODO: This wrapper is duplicated by all clients and should be part of the HotelAvailabilityMapTooltip component.
        <Box
          className={clsx({
            "premier-collection": showPCStyling,
            "lifestyle-collection": showLCStyling,
          })}
          display="flex"
          flexDirection="column"
          alignItems="center"
          borderRadius="9px"
          marginBottom="5px"
          boxShadow="0px 0px 10px 2px var(--backdrop-shadow)"
          style={{
            backgroundColor: "var(--white)",
          }}
        >
          {showPCStyling && <Icon name={IconName.PremierCollectionRibbon} />}
          {showLCStyling && <Icon name={IconName.LifestyleCollectionRibbon} />}
          <HotelAvailabilityMapTooltip
            lodging={lodging}
            onCloseClick={onMarkerClose}
            onClickHotelDetails={onMarkerDetailsClick}
            nightCount={searchedNightCount}
            rewardsKey={rewardsKey}
            isLodgingPromotionsExperiment={isLodgingPromotionsExperiment}
            isVentureX={isVentureX}
            largestValueAccount={largestValueAccount}
            onViewCarouselImage={() => {
              trackEvent({
                eventName: HOTEL_LIST_MAP_CAROUSEL,
                properties: {
                  map_funnel: MapFunnelEnum.STAYS,
                },
              });
            }}
            policyLimit={policies?.hotels.policies[0].maxPricePerNight}
            tenant={config.TENANT}
            searchedRoomsCount={isCorporate ? roomsCount : undefined}
            onViewCorpCompareBar={onOpenCompareBarTooltip(
              ViewedCorpRateDescriptorEntryPoints.HOTELS_LIST_MAP
            )}
            showPolicyBanner={showPolicyBanner}
            isPayAtHotelBadgeEnabled={isPayAtHotelBadgeEnabled}
            wishlistComponent={
              isWishlistExperiment ? (
                <WishlistToggleHeart
                  listingId={lodging.lodging.id}
                  listingImageUrl={lodging.lodging.media?.[0]?.url}
                  stayType={isHome ? StayType.Homes : StayType.Hotels}
                  lodgingCollection={lodging.lodgingCollection}
                  style={{
                    position: "absolute",
                    top: "20px",
                    right: "52px",
                    zIndex: 1,
                  }}
                />
              ) : undefined
            }
          />
        </Box>
      )}

      {showPCStyling || showLCStyling ? (
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          className={clsx("premium-map-marker", {
            selected: isSelected || forceHoverState,
            "grey-overrides": isCorporate && !isInPolicy,
          })}
          onClick={handleMarkerClick}
        >
          <Icon
            name={IconName.PremierCollectionPricePin}
            className="icon-stroke icon-hover-fill icon-svg-drop-shadow"
          />
          <Box className="icon-container"
            position="absolute" 
            top="2px"
            display="flex"
            alignItems="center"
            justifyContent="center">
            {renderPCLCIcon()}
            {renderPrice()}
          </Box>
        </Box>
      ) : (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          className={clsx("default-map-marker", {
            selected: isSelected || forceHoverState,
            "green-overrides": isSignificantSavings(
              lodging.price,
              lodging.bestPromotionThisLodging
            ),
            "grey-overrides": isCorporate && !isInPolicy,
          })}
          onClick={handleMarkerClick}
        >
          {renderIcon()}
          {renderPrice()}
        </Box>
      )}
    </Box>
  );
};
